import { useState } from 'react'

import { Box, Button, Icon, Tag, Text } from '@factorialco/deprecated-design-system'

import type {
  ResultMatches,
  SupplementsMatches,
  VisibleButtons,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'

interface Props {
  index: number
  visibleButtons: VisibleButtons[]
  worksheetMatches: ResultMatches | SupplementsMatches
  activeButton: string
  worksheetData: WorksheetData
  sequentialSelection: boolean
  onResetButton: (buttonName: string) => () => void
  onSetActiveButton: (buttonName: string) => void
}

const WorksheetButton = ({
  index,
  visibleButtons,
  worksheetMatches,
  activeButton,
  worksheetData,
  sequentialSelection,
  onResetButton,
  onSetActiveButton,
}: Props) => {
  const button = visibleButtons[index]
  const [hover, setHover] = useState(false)

  if (!button) return

  const buttonName = Object.keys(button)[0] ?? ''
  const worksheetMatch = worksheetMatches[buttonName]
  const isCurrentButton = activeButton === buttonName

  const renderMatchedButton = (cellValue: string | number | undefined) =>
    sequentialSelection ? (
      <Box
        key={buttonName}
        flexDirection="row"
        gap="s8"
        alignItems="center"
        background={isCurrentButton || hover ? 'grey200' : 'transparent'}
        borderRadius={{ all: 'abs008' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onResetButton(buttonName)}
        padding="s8"
        maxWidth="s240"
      >
        <Box paddingX="s4" paddingY="s4" background="viridian800" borderRadius={{ all: 'abs008' }}>
          <Icon.Small icon="check" color="white" />
        </Box>
        <Box style={{ width: '85%' }}>
          <Text.Normal size="200" weight="medium" ellipsis>
            {button[buttonName]}
          </Text.Normal>
          <Text.Normal size="100" weight="medium" ellipsis color="grey600">
            {cellValue}
          </Text.Normal>
        </Box>
      </Box>
    ) : (
      <Box key={buttonName}>
        <Tag rightItem="close" onClose={onResetButton(buttonName)}>{`${button[buttonName]}: ${
          cellValue || ''
        }`}</Tag>
      </Box>
    )

  const renderButton = () =>
    sequentialSelection ? (
      <Box
        key={buttonName}
        flexDirection="row"
        gap="s8"
        alignItems="center"
        borderRadius={{ all: 'abs008' }}
        background={isCurrentButton || hover ? 'grey200' : 'transparent'}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        padding="s8"
        onClick={() => !isCurrentButton && onSetActiveButton(buttonName)}
      >
        <Box
          paddingX="s8"
          paddingY="s2"
          background={isCurrentButton ? 'darker800' : hover ? 'grey400' : 'grey200'}
          borderRadius={{ all: 'abs008' }}
        >
          <Text.Normal size="200" weight="semibold" color={isCurrentButton ? 'white' : 'grey800'}>
            {index + 1}
          </Text.Normal>
        </Box>
        <Box style={{ width: '85%' }}>
          <Box flexDirection="row" gap="s4" alignItems="center">
            <Text.Normal size="200" weight="medium" ellipsis>
              {`${button[buttonName]}`}
            </Text.Normal>
            {button.required && <Icon.Small icon="alert-circle" color="grey800" />}
          </Box>
        </Box>
      </Box>
    ) : (
      <Button
        key={buttonName}
        onClick={() => onSetActiveButton(buttonName)}
        type={activeButton === buttonName ? 'primary' : 'secondary'}
      >
        {`${button[buttonName]} ${button.required ? '*' : ''}`}
      </Button>
    )

  if (worksheetMatch) {
    const rowIndex = worksheetMatch[0]
    const colIndex = worksheetMatch[1]
    const cellValue = worksheetData[rowIndex]?.[colIndex]

    return renderMatchedButton(cellValue)
  } else {
    return renderButton()
  }
}

export default WorksheetButton
